<template>
  <LayoutGrid
    v-if="grid"
    ref="gridRef"
    class="sdv-activation-preview-list-grid"
    :grid="grid"
    style="pointer-events: none;"
  >
    <template #default="{ area }">
      <slot
        :items="itemsMap[area]"
        :options="getOptions(area)"
        :on="listListeners"
      >
        <ActivationPreviewList
          :items="itemsMap[area]"
          :options="getOptions(area)"
          style="pointer-events: all;"
          :style="getListStyles(area)"
          :animation="animation"
          v-on="listListeners"
        />
      </slot>
    </template>
  </LayoutGrid>
</template>

<script lang="ts">
import { PropType, defineComponent, ref } from 'vue-demi'
import ActivationPreviewList, { props } from './ActivationPreviewList.vue'
import { ActivationListContext, ExperienceActivationPayload } from '../types'
import { defineActivationListEmits, useBypassActivationListEvents } from '../composables/use-activation-list-events'
import { LayoutGrid, LayoutGridConfig } from '@source-digital/layout-grid-vue'

const emits = defineActivationListEmits()

type StyleValue = string | Record<string, any> | StyleValue[]
type ValueMapResolver<T> = T | Map<string, T> | ((area: string) => T)

function resolveAreaValue<Return> (prop: ValueMapResolver<Return> | undefined, area: string): Return | undefined {
  if (prop == null) return undefined
  if (prop instanceof Function) {
    return prop(area)
  } else if (prop instanceof Map) {
    return prop.get(area)
  } else {
    return prop
  }
}

export default defineComponent({
  name: 'ActivationPreviewListGrid',
  components: {
    LayoutGrid,
    ActivationPreviewList
  },
  props: {
    animation: props.animation,
    grid: {
      type: Object as PropType<LayoutGridConfig | undefined>,
      required: false,
      default: () => undefined
    },
    itemsMap: {
      type: Object as PropType<Record<string, ExperienceActivationPayload[] | undefined>>,
      required: false,
      default: () => ({})
    },
    options: {
      type: [Object, Function] as PropType<ValueMapResolver<ActivationListContext>>,
      required: false,
      default: () => (area: string) => {
        const isLeft = area.includes('left')
        const isRight = area.includes('right')
        return {
          portrait: false,
          align: isLeft ? 'left' : isRight ? 'right' : 'center',
          left: isLeft
        }
      }
    },
    listStyle: {
      type: [Object, Function] as PropType<ValueMapResolver<StyleValue>>,
      required: false,
      default: () => ({})
    }
  },
  emits,
  setup (props, context) {
    const gridRef = ref<InstanceType<typeof LayoutGrid>>()

    function getOptions (area: string) {
      return resolveAreaValue(props.options, area)
    }
    function getListStyles (area: string) {
      return resolveAreaValue(props.listStyle, area)
    }
    return {
      gridRef,
      findElementByArea (...args: Parameters<InstanceType<typeof LayoutGrid>['findElementByArea']>) {
        if (gridRef.value == null) throw new Error('gridRef is null')
        return gridRef.value.findElementByArea(...args)
      },
      getOptions,
      getListStyles,
      listListeners: useBypassActivationListEvents(context)
    }
  }
})
</script>

<style>

</style>
